import type { FC } from 'react'
import { Box, Divider, ListItem, Typography } from '@mui/material'
import { useNavigate, useLocation } from 'react-router-dom'

import { SideMenuBackLink } from './SideMenuBackLink.js'
import { SideMenuListItem } from './SideMenuListItem.js'
import { MenuItemWithSubItems } from './types.js'

type Props = {
  disableTab: boolean
  isExpanded: boolean
  menuItems: MenuItemWithSubItems[]
  navigationPath?: string
}

const getMenuItem = (menuItems: MenuItemWithSubItems[], link?: string): MenuItemWithSubItems | undefined => {
  for (let index = 0; index < menuItems.length; index += 1) {
    const currentNode = menuItems[index]

    if (currentNode.link === link) {
      return currentNode
    }

    if (currentNode.subMenuItems) {
      const result = getMenuItem(currentNode.subMenuItems, link)
      if (result) {
        return result
      }
    }
  }
  return undefined
}

const getAllSelectedMenuItems = (menuItems: MenuItemWithSubItems[], path: string) => {
  const navigationPath = path.toLowerCase().replace(/^\/|\/$/g, '')
  const navPathKeys = navigationPath.split('/') ?? []
  const pathToRoot = navPathKeys.map((_, index, navPathSegments) => {
    const i = index === 0 ? navPathKeys.length : index * -1
    return `/${navPathSegments.slice(0, i).join('/')}`
  })
  return pathToRoot
    .reverse()
    .map((link) => getMenuItem(menuItems, link))
    .filter((item) => item !== undefined)
}

export const SideMenuNodes: FC<Props> = ({ disableTab, isExpanded, menuItems, navigationPath }) => {
  const navigation = useNavigate()
  const { pathname } = useLocation()

  const selectedMenuItems = getAllSelectedMenuItems(menuItems, navigationPath ?? pathname)

  const rootApp =
    selectedMenuItems.length > 0 && selectedMenuItems[0]?.rootApp ? `/${selectedMenuItems[0]?.rootApp}` : '/'

  const topLevelNode = selectedMenuItems.slice(0, -2).pop()
  const parentNode = selectedMenuItems.slice(0, -1).pop()
  const selectedNode = selectedMenuItems.pop()

  const subMenuNodes = selectedNode?.subMenuItems ?? parentNode?.subMenuItems
  const navigateBackUrl = topLevelNode?.link ?? rootApp
  const tabIndexProp = disableTab ? { tabIndex: -1 } : {}

  return (
    <>
      {subMenuNodes && (
        <>
          {navigateBackUrl && <SideMenuBackLink onNavigateBack={() => navigation(navigateBackUrl)} {...tabIndexProp} />}
          <Divider />
          <ListItem data-testid="side-navigation-title" {...tabIndexProp}>
            <Box py={1}>
              <Typography>{isExpanded ? (parentNode?.label ?? selectedNode?.label) : ''}</Typography>
            </Box>
          </ListItem>
        </>
      )}

      {(subMenuNodes ?? menuItems)?.map((menuItem: MenuItemWithSubItems) => {
        const item = {
          ...menuItem,
          active:
            menuItem.active !== undefined && !subMenuNodes ? menuItem.active : menuItem.link === selectedNode?.link,
        }

        return <SideMenuListItem isExpanded={isExpanded} disableTab={disableTab} key={item.key} menuItem={item} />
      })}
    </>
  )
}
